import { useState } from 'react';
import type { CheckConfigField, CheckRegistryRow } from '@fkwd-qc/shared';
import { useRunClientCheck } from '@/hooks/use-check-runs';
import { useClientCheckSettings, useUpdateClientCheckSettings } from '@/hooks/use-client-check-settings';
import { useSession } from '@/hooks/use-session';
import { toConfigDraft, type ConfigDraft } from '@/lib/check-config';
import { fieldControlProps } from '@/lib/field-attributes';
import { FormField } from './form-field';
import { InlineError } from './inline-error';
import { OptionSelect } from './option-select';
import { ToggleSwitch } from './toggle-switch';

/**
 * Props for the per-client controls of one check.
 *
 * @since 1.5.0
 */
export interface CheckControlsProps {
    clientSlug: string;
    clientName: string;
    /** the registry row for the check, or undefined while the registry loads or for an unknown slug */
    check: CheckRegistryRow | undefined;
}

interface ConfigFieldControlProps {
    controlId: string;
    field: CheckConfigField;
    value: string;
    onChange: (value: string) => void;
}

/**
 * One declared setting drawn as the control its type asks for.
 *
 * @since 1.5.0
 * @param props - the field, its control id, its current text and the change handler
 * @returns the field element
 */
function ConfigFieldControl({ controlId, field, value, onChange }: ConfigFieldControlProps) {
    const hasHelp = field.help !== undefined;

    return (
        <FormField controlId={controlId} description={field.help} label={field.label}>
            {field.type === 'select' ? (
                <OptionSelect
                    {...fieldControlProps(controlId, null, hasHelp)}
                    className="fkwd-field__input"
                    onValueChange={onChange}
                    options={field.options ?? []}
                    placeholder="Default"
                    value={value}
                />
            ) : (
                <input
                    {...fieldControlProps(controlId, null, hasHelp)}
                    className="fkwd-field__input"
                    inputMode={field.type === 'number' ? 'decimal' : undefined}
                    onChange={(event) => onChange(event.target.value)}
                    placeholder={field.placeholder}
                    type={field.type === 'number' ? 'number' : 'text'}
                    value={value}
                />
            )}
        </FormField>
    );
}

/**
 * The controls the drawer offers for one check on one client: run it now, switch it on or off
 * for this client, and edit the overrides the check declares. What shows depends on the
 * signed-in role; a planned check offers nothing.
 *
 * @since 1.5.0
 * @param props - the client, its display name and the check's registry row
 * @returns the controls, or nothing when the role or the check allows none
 */
export function CheckControls({ clientSlug, clientName, check }: CheckControlsProps) {
    const { can } = useSession();
    const isRunnable = check !== undefined && check.state !== 'planned';
    const canRun = can('dev') && isRunnable;
    const canManage = can('admin') && isRunnable;

    const runNow = useRunClientCheck();
    const settings = useClientCheckSettings(canManage ? clientSlug : undefined, check?.slug);
    const update = useUpdateClientCheckSettings();

    // the form shows what is stored plus whatever has been typed since; a save clears the typed layer
    const [edits, setEdits] = useState<ConfigDraft>({});

    if (check === undefined || (!canRun && !canManage)) {
        return null;
    }

    const fields = check.configFields ?? [];
    const draft: ConfigDraft = { ...toConfigDraft(fields, settings.data?.config ?? {}), ...edits };
    const isEnabled = settings.data?.isEnabled ?? true;
    const runLine = runNow.data === undefined ? '' : `${runNow.data.status}: ${runNow.data.summary}`;

    const save = (nextEnabled: boolean): void => {
        update.mutate(
            { clientSlug, checkSlug: check.slug, body: { isEnabled: nextEnabled, config: draft } },
            { onSuccess: () => setEdits({}) },
        );
    };

    return (
        <>
            {canRun ? (
                <>
                    <div className="fkwd-label fkwd-label--spaced">Actions</div>
                    <div className="fkwd-checklist__actions">
                        <button
                            className="fkwd-btn"
                            disabled={runNow.isPending}
                            onClick={() => runNow.mutate({ clientSlug, checkSlug: check.slug })}
                            type="button"
                        >
                            {runNow.isPending ? 'Running...' : 'Run now'}
                        </button>
                        <span aria-live="polite" className="fkwd-checklist__result" role="status">
                            {runLine}
                        </span>
                    </div>
                    <InlineError error={runNow.error} />
                </>
            ) : null}

            {canManage ? (
                <form
                    onSubmit={(event) => {
                        event.preventDefault();
                        save(isEnabled);
                    }}
                >
                    <div className="fkwd-label fkwd-label--spaced">For {clientName}</div>
                    <ToggleSwitch
                        isChecked={isEnabled}
                        isDisabled={settings.data === undefined || update.isPending}
                        label={`Run ${check.name} for ${clientName}`}
                        onToggle={save}
                    />
                    {fields.map((field) => {
                        const controlId = `${clientSlug}-${check.slug}-${field.key}`;
                        return (
                            <ConfigFieldControl
                                controlId={controlId}
                                field={field}
                                key={field.key}
                                onChange={(value) => setEdits((previous) => ({ ...previous, [field.key]: value }))}
                                value={draft[field.key] ?? ''}
                            />
                        );
                    })}
                    {fields.length === 0 ? null : (
                        <div className="fkwd-checklist__actions">
                            <button
                                className="fkwd-btn"
                                disabled={settings.data === undefined || update.isPending}
                                type="submit"
                            >
                                {update.isPending ? 'Saving...' : 'Save settings'}
                            </button>
                            <span aria-live="polite" className="fkwd-checklist__result" role="status">
                                {update.isSuccess ? 'Saved.' : ''}
                            </span>
                        </div>
                    )}
                    <InlineError error={update.error ?? settings.error} />
                </form>
            ) : null}
        </>
    );
}
